import type { ColDef, ColGroupDef } from 'ag-grid-community'
import type { DagColumnMeta, DropdownOptionRead } from '@/types/dag'
import { isCellEditable } from './DagCellRenderer'
import { DagConditionalHeader } from './DagConditionalHeader'
import {
  DagColumnHeaderWithTotals,
  type ColumnTotal,
} from './DagColumnHeaderWithTotals'
import { HIDDEN_ATTRIBUTE_IDS } from './columnConfiguration'
import type { RowData } from './useProductsTableState'

// Columns handled by fixed renderers — skip from dynamic generation
const FIXED_COLUMN_IDS = new Set(['description', 'plan_sku_num'])

export interface BuildDagColumnDefsOptions {
  columns: DagColumnMeta[]
  groups: string[]
  dropdownMap: Record<string, DropdownOptionRead[]>
  hiddenColumnIds?: Set<string>
  isReadOnly?: boolean
  showActions?: boolean
  columnTotals?: Record<string, ColumnTotal[]>
  // Fixed column components (provided by ProductsTable)
  NameHeaderComponent: React.ComponentType
  NameCellRenderer: React.ComponentType<{ data?: RowData }>
  SkuCellRenderer: (props: { data?: RowData }) => React.ReactNode
  ActionsCellRenderer: (props: { data?: RowData }) => React.ReactNode
  DagCellRendererComponent: React.ComponentType<{
    data?: RowData
    colDef?: { colId?: string }
    columnMeta: DagColumnMeta
    dropdownOptions?: DropdownOptionRead[]
  }>
}

export function buildDagColumnDefs(
  opts: BuildDagColumnDefsOptions
): (ColDef<RowData> | ColGroupDef<RowData>)[] {
  const {
    columns,
    groups,
    dropdownMap,
    hiddenColumnIds,
    columnTotals,
    NameHeaderComponent,
    NameCellRenderer,
    SkuCellRenderer,
    ActionsCellRenderer,
    DagCellRendererComponent,
  } = opts

  // 1. Build the fixed "Item" group with Description + SKU (metadata-driven)
  const descMeta = columns.find(c => c.id === 'description')
  const skuMeta = columns.find(c => c.id === 'plan_sku_num')
  const skuTotals = columnTotals?.['plan_sku_num']

  const itemGroup: ColGroupDef<RowData> = {
    headerName: 'Item',
    headerClass: 'ag-group-header-center',
    suppressStickyLabel: true,
    children: [
      {
        colId: 'description',
        headerName: descMeta?.label ?? 'Description',
        headerComponent: NameHeaderComponent,
        initialWidth: descMeta?.width || 400,
        pinned: 'left',
        lockPinned: true,
        cellRenderer: NameCellRenderer,
        cellClassRules: {
          'dag-cell-readonly': params => {
            if (!params.data || params.data.isOptionRow) return false
            if (opts.isReadOnly) return true
            const cells = params.data.cells ?? {}
            const sku = cells['plan_sku_num']
            const isNewSku = !!cells['is_new_sku']
            return !!sku && !isNewSku
          },
        },
        sortable: true,
        resizable: true,
        filter: false,
        suppressKeyboardEvent: () => true,
        valueGetter: params => params.data?.cells?.['description'] ?? '',
      },
      {
        colId: 'plan_sku_num',
        headerName: skuMeta?.label ?? 'SKU #',
        initialWidth: skuMeta?.width || 130,
        pinned: 'left',
        lockPinned: true,
        cellRenderer: SkuCellRenderer,
        resizable: true,
        filter: false,
        suppressKeyboardEvent: () => true,
        valueGetter: params => params.data?.cells?.['plan_sku_num'] ?? '',
        ...(skuTotals && skuTotals.length > 0
          ? {
              headerComponent: DagColumnHeaderWithTotals,
              headerComponentParams: { totals: skuTotals },
            }
          : {}),
      },
    ],
  }

  // 2. Build dynamic column groups from DAG metadata
  const dynamicColumns = columns.filter(
    c =>
      c.kind !== 'aggregate' &&
      !FIXED_COLUMN_IDS.has(c.id) &&
      !HIDDEN_ATTRIBUTE_IDS.has(c.id) &&
      !hiddenColumnIds?.has(c.id)
  )

  // Group columns by their `group` field
  const columnsByGroup = new Map<string, DagColumnMeta[]>()
  for (const col of dynamicColumns) {
    const groupCols = columnsByGroup.get(col.group) ?? []
    groupCols.push(col)
    columnsByGroup.set(col.group, groupCols)
  }

  // Build grouped column defs in the order provided by `groups`
  const dynamicGroups: ColGroupDef<RowData>[] = []

  for (const groupName of groups) {
    const groupCols = columnsByGroup.get(groupName)
    if (!groupCols || groupCols.length === 0) continue

    // Skip the "Identity" group as it's handled by fixed columns
    if (groupName === 'Identity') continue

    const children: ColDef<RowData>[] = groupCols.map(meta => {
      const colDef: ColDef<RowData> = {
        colId: meta.id,
        headerName: meta.label,
        cellRenderer: DagCellRendererComponent,
        cellRendererParams: {
          columnMeta: meta,
          dropdownOptions: meta.dropdown_category
            ? (dropdownMap[meta.dropdown_category] ??
              meta.options?.map(o => ({ value: o })))
            : undefined,
        },
        cellClassRules: {
          // Light blue: conditionally editable AND currently editable
          'dag-cell-conditional': params => {
            if (!params.data || params.data.isOptionRow) return false
            if (opts.isReadOnly) return false
            // num_stores is editable when no store model — highlight with light blue
            if (
              meta.id === 'num_stores' &&
              !params.data.cells?.['store_model_id']
            ) {
              return true
            }
            if (meta.editable_when !== 'conditional') return false
            return isCellEditable(meta, params.data.cells ?? {})
          },
          // Darker blue: conditionally editable but currently NOT editable
          'dag-cell-conditional-locked': params => {
            if (!params.data || params.data.isOptionRow) return false
            if (opts.isReadOnly) return false
            if (meta.editable_when !== 'conditional') return false
            return !isCellEditable(meta, params.data.cells ?? {})
          },
          'dag-cell-readonly': params => {
            if (!params.data || params.data.isOptionRow) return false
            if (opts.isReadOnly) return true
            // Conditional cells get blue instead of gray
            if (meta.editable_when === 'conditional') return false
            return !isCellEditable(meta, params.data.cells ?? {})
          },
        },
        suppressKeyboardEvent: () => true,
        sortable: false,
        resizable: true,
        filter: false,
      }

      const totalsForCol = columnTotals?.[meta.id]
      if (totalsForCol && totalsForCol.length > 0) {
        colDef.headerComponent = DagColumnHeaderWithTotals
        colDef.headerComponentParams = { totals: totalsForCol }
      } else if (meta.editable_when === 'conditional') {
        const tooltipText =
          meta.editable_condition_description ?? meta.editable_condition
        if (tooltipText) {
          colDef.headerComponent = DagConditionalHeader
          colDef.headerComponentParams = { tooltipText }
        }
      }

      return colDef
    })

    dynamicGroups.push({
      headerName: groupName,
      headerClass: 'ag-group-header-center',
      children,
    })
  }

  // 3. Fixed right-pinned column: Actions (optional)
  if (opts.showActions !== false) {
    const actionsCol: ColDef<RowData> = {
      colId: 'actions',
      headerName: 'Actions',
      pinned: 'right',
      cellRenderer: ActionsCellRenderer,
      headerClass: 'ag-pinned-header',
      sortable: false,
    }
    return [itemGroup, ...dynamicGroups, actionsCol]
  }

  return [itemGroup, ...dynamicGroups]
}
